import { io } from "socket.io-client";
import {
  APP_TYPE,
  PARENT_COMPANY,
  REFRESH_TOKEN_NAME,
  ROOT_API_URL,
  TOKEN_NAME,
  USER,
} from "../config/defaultSettings";

/**
 * Returns the API origin used by realtime socket connections.
 * ROOT_API_URL includes `/api/v1`, which Socket.IO should not receive.
 */
export const getSocketUrl = () => {
  const base = ROOT_API_URL.replace(/\/api\/v1\/?$/, "");
  if (base.startsWith("/") && typeof window !== "undefined") {
    return window.location.origin;
  }
  return base;
};

/**
 * Returns the current auth token for socket connections.
 * Prefer the cookie-backed session token, but preserve the existing
 * localStorage fallback for backwards compatibility while older flows
 * are still being retired.
 */
export const getSocketAuthToken = () => {
  try {
    const cookieMatch = document.cookie
      .split("; ")
      .find((row) => row.startsWith(`${TOKEN_NAME}=`));

    if (cookieMatch) {
      return decodeURIComponent(cookieMatch.split("=")[1]);
    }
  } catch {
    // Ignore cookie parsing issues and fall back below.
  }

  try {
    return window?.localStorage?.getItem(TOKEN_NAME) || null;
  } catch {
    return null;
  }
};

/**
 * Clear the client-side auth state used by legacy socket and auth flows.
 * This keeps logout behavior aligned with the transitional cookie/localStorage
 * reads that still exist while the app finishes moving to one auth source.
 */
export const clearClientAuthState = () => {
  try {
    document.cookie = `${TOKEN_NAME}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
    document.cookie = `${REFRESH_TOKEN_NAME}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
  } catch {
    // Ignore cookie cleanup failures in non-browser contexts.
  }

  try {
    const storage = window?.localStorage;
    [TOKEN_NAME, REFRESH_TOKEN_NAME, USER, PARENT_COMPANY, APP_TYPE].forEach(
      (key) => storage?.removeItem(key)
    );
  } catch {
    // Ignore storage cleanup failures and rely on server-side logout.
  }
};

/**
 * Local dev against AWS beta: Socket.IO through Vite → ALB is unreliable (EPIPE).
 * REST via /api proxy works; realtime is off unless VITE_ENABLE_AWS_BETA_SOCKET=true.
 */
const shouldSkipRemoteSocket = () =>
  import.meta.env.DEV &&
  import.meta.env.VITE_USE_AWS_BETA_API === "true" &&
  import.meta.env.VITE_ENABLE_AWS_BETA_SOCKET !== "true";

/** Minimal stub so contexts can register listeners without opening a connection. */
const createNoopSocket = () => {
  const socket = {
    id: null,
    connected: false,
    on() {
      return socket;
    },
    off() {
      return socket;
    },
    emit() {
      return socket;
    },
    close() {
      return socket;
    },
    disconnect() {
      return socket;
    },
  };
  return socket;
};

/**
 * Create a socket client with the shared auth and transport defaults.
 */
export const createSocketClient = (options = {}) => {
  if (shouldSkipRemoteSocket()) {
    if (typeof console !== "undefined") {
      console.info(
        "[attunelogic] Socket.IO disabled for AWS beta local dev (REST only). Set VITE_ENABLE_AWS_BETA_SOCKET=true to try realtime."
      );
    }
    return createNoopSocket();
  }

  const auth = {
    token: getSocketAuthToken(),
    ...(options.auth || {}),
  };

  return io(getSocketUrl(), {
    withCredentials: true,
    transports: ["websocket", "polling"],
    ...options,
    auth,
  });
};

export default {
  clearClientAuthState,
  createSocketClient,
  getSocketUrl,
  getSocketAuthToken,
};
